"use client"

import * as React from "react"
import { AppIcon } from "@/components/desk/AppIcon"
import { OpenAppTab } from "@/types/workspace"
import { toast } from "sonner"
import { Trash2, Smartphone, X } from "lucide-react"

interface MobileAppSwitcherProps {
  isOpen: boolean
  onClose: () => void
  openApps: OpenAppTab[]
  setActiveAppId: (id: string) => void
  handleCloseApp: (instanceId: string) => void
  switcherOffsets: Record<string, number>
  handleSwitcherTouchStart: (e: React.TouchEvent, instanceId: string) => void
  handleSwitcherTouchMove: (e: React.TouchEvent, instanceId: string) => void
  handleSwitcherTouchEnd: (e: React.TouchEvent, instanceId: string) => void
  theme?: string
}

export function MobileAppSwitcher({
  isOpen,
  onClose,
  openApps,
  setActiveAppId,
  handleCloseApp,
  switcherOffsets,
  handleSwitcherTouchStart,
  handleSwitcherTouchMove,
  handleSwitcherTouchEnd,
  theme,
}: MobileAppSwitcherProps) {
  if (!isOpen) return null

  const isLight = theme === "light"

  const handleCloseAllApps = () => {
    openApps.forEach((tab) => handleCloseApp(tab.instanceId))
    onClose()
    toast.success("Todos os aplicativos foram fechados.")
  }

  return (
    <div className={`fixed inset-0 z-55 flex flex-col backdrop-blur-xl animate-in fade-in duration-300 font-sans select-none ${isLight ? "bg-zinc-100/95 text-zinc-900" : "bg-zinc-950/90 text-white"
      }`}>
      <div className="absolute inset-0 z-0" onClick={onClose} />

      {/* Header */}
      <div className="relative z-10 px-6 pt-8 pb-3 flex justify-between items-center shrink-0 border-b border-zinc-200/50 dark:border-white/5">
        <div>
          <h3 className="text-base font-extrabold flex items-center gap-2">
            Segundo Plano
          </h3>
        </div>

        <div className="flex items-center gap-2">
          {openApps.length > 0 && (
            <button
              onClick={handleCloseAllApps}
              className="flex items-center gap-1.5 px-2 py-1 rounded-xl bg-red-500/15 border border-red-500/30 text-red-500 text-xs font-bold hover:bg-red-500/25 active:scale-95 transition-all cursor-pointer shadow-xs"
              title="Fechar todos os aplicativos"
            >
              <Trash2 className="size-3.5" />
              <span>Fechar Todos</span>
            </button>
          )}

          <button
            onClick={onClose}
            className={`p-1.5 rounded-full cursor-pointer active:scale-90 transition-all ${isLight ? "bg-zinc-200 hover:bg-zinc-300 text-zinc-700" : "bg-white/10 hover:bg-white/20 text-white"
              }`}
          >
            <X className="size-4" />
          </button>
        </div>
      </div>

      {/* Carousel de Apps */}
      <div className="relative z-10 flex-1 flex items-center overflow-x-auto gap-6 px-10 py-4 snap-x snap-mandatory scrollbar-none">
        {openApps.map((tab) => {
          const offset = switcherOffsets[tab.instanceId] || 0
          const opacity = Math.max(0.2, 1 - offset / 200)

          return (
            <div
              key={tab.instanceId}
              onTouchStart={(e) => handleSwitcherTouchStart(e, tab.instanceId)}
              onTouchMove={(e) => handleSwitcherTouchMove(e, tab.instanceId)}
              onTouchEnd={(e) => handleSwitcherTouchEnd(e, tab.instanceId)}
              className="snap-center shrink-0 flex flex-col items-center gap-4 relative"
              style={{
                transform: `translateY(-${offset}px)`,
                opacity: opacity,
                transition: offset === 0 ? "transform 0.3s ease, opacity 0.3s ease" : "none",
              }}
            >
              {offset > 30 && (
                <div className="absolute -top-8 text-red-500 text-xs font-bold animate-bounce flex flex-col items-center">
                  <span>↑</span>
                  <span>Solte para Fechar</span>
                </div>
              )}

              <div
                onClick={() => {
                  if (offset === 0) {
                    setActiveAppId(tab.instanceId)
                    onClose()
                  }
                }}
                className={`w-[210px] h-[330px] rounded-3xl border flex flex-col overflow-hidden transition-all cursor-pointer relative shadow-2xl ${isLight
                  ? "bg-white border-zinc-200 hover:border-zinc-300 text-zinc-900"
                  : "bg-zinc-900 border-white/10 hover:border-white/20 text-white"
                  }`}
              >
                <div className={`px-4 py-3 flex items-center gap-2 border-b shrink-0 ${isLight ? "bg-zinc-100/90 border-zinc-200" : "bg-zinc-800/80 border-white/5"
                  }`}>
                  <AppIcon iconUrl={tab.app.iconUrl} iframeUrl={tab.app.iframeUrl} name={tab.app.name} size="small" />
                  <span className="text-xs font-bold truncate flex-1">{tab.app.name}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleCloseApp(tab.instanceId)
                      toast.info(`${tab.app.name} foi fechado.`)
                    }}
                    className="text-[9px] font-bold text-red-500 bg-red-500/10 px-2 py-0.5 rounded-full hover:bg-red-500/20 active:scale-95 cursor-pointer"
                  >
                    X
                  </button>
                </div>

                <div className={`flex-1 p-4 flex flex-col items-center justify-center text-center ${isLight ? "bg-zinc-50/70" : "bg-zinc-950"
                  }`}>
                  <div className={`size-16 rounded-2xl border flex items-center justify-center shadow-sm mb-3 ${isLight ? "bg-white border-zinc-200" : "bg-zinc-900 border-white/10"
                    }`}>
                    <AppIcon iconUrl={tab.app.iconUrl} iframeUrl={tab.app.iframeUrl} name={tab.app.name} size="large" />
                  </div>
                  <span className="text-[11px] font-bold opacity-80">{tab.app.name}</span>
                  <span className="text-[9px] opacity-50 mt-1 italic">Toque para alternar</span>
                </div>
              </div>
            </div>
          )
        })}

        {openApps.length === 0 && (
          <div className="w-full flex flex-col items-center justify-center text-center p-8 z-10 opacity-70">
            <Smartphone className="size-10 text-blue-500 mb-2" />
            <span className="text-xs font-bold">Nenhum aplicativo em segundo plano</span>
            <span className="text-[10px] opacity-60 mt-0.5">Abra apps pelo Desk para ver a lista de tarefas.</span>
          </div>
        )}
      </div>

      <div className="py-6 flex justify-center z-10 shrink-0">
        <button onClick={onClose} className={`w-32 h-1 rounded-full cursor-pointer ${isLight ? "bg-zinc-300" : "bg-white/30"
          }`} title="Voltar ao início" />
      </div>
    </div>
  )
}
